<template>
  <div class="settings-page">
    <!-- iOS 18 风格大标题导航栏 -->
    <div class="nav-header">
      <button class="back-btn" @click="goBack">
        <svg width="12" height="20" viewBox="0 0 12 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 2L2 10L10 18" stroke="#007AFF" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span>返回</span>
      </button>
    </div>
    <div class="page-title">设置</div>

    <!-- 设置列表 -->
    <div class="settings-section">
      <div class="section-header">账号与安全</div>
      <div class="settings-card">
        <div class="settings-item" @click="openPasswordModal">
          <div class="item-left">
            <div class="item-icon password-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="11" width="18" height="11" rx="2" stroke="#fff" stroke-width="2"/>
                <path d="M7 11V7C7 4.23858 9.23858 2 12 2C14.7614 2 17 4.23858 17 7V11" stroke="#fff" stroke-width="2" stroke-linecap="round"/>
                <circle cx="12" cy="16" r="1.5" fill="#fff"/>
              </svg>
            </div>
            <span class="item-label">修改密码</span>
            <span v-if="isGuest" class="item-disabled-hint">（访客不可用）</span>
          </div>
          <div class="item-right">
            <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1L7 7L1 13" stroke="#C7C7CC" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </div>
        <div class="settings-item" v-if="!isGuest" @click="showGuestPasswordModal = true">
          <div class="item-left">
            <div class="item-icon guest-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="8" r="4" stroke="#fff" stroke-width="2"/>
                <path d="M4 20C4 16.6863 7.58172 14 12 14C16.4183 14 20 16.6863 20 20" stroke="#fff" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </div>
            <span class="item-label">访客密码</span>
            <span class="item-status">{{ hasGuestPassword ? '已设置' : '未设置' }}</span>
          </div>
          <div class="item-right">
            <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1L7 7L1 13" stroke="#C7C7CC" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- 访客权限设置（仅主账号显示） -->
    <div class="settings-section" v-if="!isGuest">
      <div class="section-header">访客权限</div>
      <div class="settings-card">
        <div class="settings-item notification-item" @click="toggleGuestCanSpend">
          <div class="item-left">
            <div class="item-icon spend-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="#fff" stroke-width="2"/>
                <path d="M12 6v12M15 9.5c0-1.38-1.34-2.5-3-2.5s-3 1.12-3 2.5 1.34 2.5 3 2.5 3 1.12 3 2.5-1.34 2.5-3 2.5" stroke="#fff" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </div>
            <span class="item-label">允许访客花费余额</span>
          </div>
          <div class="item-right">
            <div class="toggle-switch" :class="{ active: guestCanSpend, loading: guestSpendLoading }">
              <div class="toggle-knob"></div>
            </div>
          </div>
        </div>
        <div class="settings-item-hint">
          关闭后，访客将无法购买接口和续费
        </div>
        <div class="settings-item notification-item" @click="toggleGuestCanRecharge">
          <div class="item-left">
            <div class="item-icon recharge-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="4" width="20" height="16" rx="2" stroke="#fff" stroke-width="2"/>
                <path d="M2 10h20" stroke="#fff" stroke-width="2"/>
                <path d="M6 15h4" stroke="#fff" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </div>
            <span class="item-label">允许访客充值</span>
          </div>
          <div class="item-right">
            <div class="toggle-switch" :class="{ active: guestCanRecharge, loading: guestRechargeLoading }">
              <div class="toggle-knob"></div>
            </div>
          </div>
        </div>
        <div class="settings-item-hint">
          关闭后，访客将无法进行充值操作
        </div>
      </div>
    </div>

    <!-- 通知设置 -->
    <div class="settings-section">
      <div class="section-header">通知设置</div>
      <div class="settings-card">
        <div class="settings-item notification-item" @click="togglePushNotification">
          <div class="item-left">
            <div class="item-icon notification-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M13.73 21a2 2 0 01-3.46 0" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <span class="item-label">消息推送通知</span>
            <span v-if="!pushSupported" class="item-disabled-hint">（当前环境不支持）</span>
          </div>
          <div class="item-right">
            <div class="toggle-switch" :class="{ active: pushEnabled, loading: pushLoading }">
              <div class="toggle-knob"></div>
            </div>
          </div>
        </div>
        <div class="settings-item-hint" v-if="pushSupported && !pushDenied">
          添加到主屏幕后，可在后台接收新消息通知
        </div>
        <div class="settings-item-hint warning" v-if="pushDenied">
          通知权限已被拒绝，请在浏览器设置中手动开启
        </div>
      </div>
    </div>

    <!-- 修改密码弹窗 -->
    <transition name="modal-fade">
      <div class="modal-overlay" v-if="showPasswordModal" @click.self="closePasswordModal">
        <div class="modal-container">
          <div class="modal-header">
            <span class="modal-title">修改密码</span>
            <button class="modal-close" @click="closePasswordModal">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 4L4 12M4 4L12 12" stroke="#8E8E93" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
          
          <div class="modal-body">
            <div class="input-group">
              <label>新密码</label>
              <div class="input-wrapper">
                <input 
                  :type="showNewPassword ? 'text' : 'password'" 
                  v-model="newPassword" 
                  placeholder="请输入新密码"
                  autocomplete="new-password"
                />
                <button class="toggle-password" @click="showNewPassword = !showNewPassword">
                  <svg v-if="showNewPassword" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 5C5.63636 5 2 12 2 12C2 12 5.63636 19 12 19C18.3636 19 22 12 22 12C22 12 18.3636 5 12 5Z" stroke="#8E8E93" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <circle cx="12" cy="12" r="3" stroke="#8E8E93" stroke-width="2"/>
                  </svg>
                  <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 3L21 21" stroke="#8E8E93" stroke-width="2" stroke-linecap="round"/>
                    <path d="M10.5 10.677C10.1888 11.0472 10 11.5018 10 12C10 13.1046 10.8954 14 12 14C12.4982 14 12.9528 13.8112 13.323 13.5" stroke="#8E8E93" stroke-width="2" stroke-linecap="round"/>
                    <path d="M7.36185 7.56118C5.68002 8.73957 4.27893 10.3462 3.3 12C4.78171 14.6396 8.0886 19 12 19C13.8849 19 15.6433 18.1652 17.0711 17" stroke="#8E8E93" stroke-width="2" stroke-linecap="round"/>
                    <path d="M12 5C15.8659 5 19.3199 8.31536 21 12C20.5557 12.8688 19.9928 13.7578 19.3 14.5" stroke="#8E8E93" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button class="btn-cancel" @click="closePasswordModal">取消</button>
            <button class="btn-confirm" @click="submitChangePassword" :disabled="isSubmitting">
              {{ isSubmitting ? '提交中...' : '确认修改' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- 访客密码设置弹窗 -->
    <transition name="modal-fade">
      <div class="modal-overlay" v-if="showGuestPasswordModal" @click.self="closeGuestPasswordModal">
        <div class="modal-container">
          <div class="modal-header">
            <span class="modal-title">设置访客密码</span>
            <button class="modal-close" @click="closeGuestPasswordModal">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 4L4 12M4 4L12 12" stroke="#8E8E93" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
          
          <div class="modal-body">
            <div class="input-group">
              <label>访客密码</label>
              <div class="input-wrapper">
                <input 
                  :type="showGuestPassword ? 'text' : 'password'" 
                  v-model="guestPassword" 
                  placeholder="设置访客登录密码（留空则清除）"
                  autocomplete="new-password"
                />
                <button class="toggle-password" @click="showGuestPassword = !showGuestPassword">
                  <svg v-if="showGuestPassword" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 5C5.63636 5 2 12 2 12C2 12 5.63636 19 12 19C18.3636 19 22 12 22 12C22 12 18.3636 5 12 5Z" stroke="#8E8E93" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <circle cx="12" cy="12" r="3" stroke="#8E8E93" stroke-width="2"/>
                  </svg>
                  <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 3L21 21" stroke="#8E8E93" stroke-width="2" stroke-linecap="round"/>
                    <path d="M10.5 10.677C10.1888 11.0472 10 11.5018 10 12C10 13.1046 10.8954 14 12 14C12.4982 14 12.9528 13.8112 13.323 13.5" stroke="#8E8E93" stroke-width="2" stroke-linecap="round"/>
                    <path d="M7.36185 7.56118C5.68002 8.73957 4.27893 10.3462 3.3 12C4.78171 14.6396 8.0886 19 12 19C13.8849 19 15.6433 18.1652 17.0711 17" stroke="#8E8E93" stroke-width="2" stroke-linecap="round"/>
                    <path d="M12 5C15.8659 5 19.3199 8.31536 21 12C20.5557 12.8688 19.9928 13.7578 19.3 14.5" stroke="#8E8E93" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                </button>
              </div>
            </div>

          </div>

          <div class="modal-footer">
            <button class="btn-cancel" @click="closeGuestPasswordModal">取消</button>
            <button class="btn-confirm" @click="submitGuestPassword" :disabled="isSubmittingGuest">
              {{ isSubmittingGuest ? '提交中...' : '确认设置' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Toast 提示 -->
    <transition name="toast-fade">
      <div class="toast" v-if="toastVisible" :class="toastType">
        {{ toastMessage }}
      </div>
    </transition>
  </div>
</template>

<script>
import { changePassword, setGuestPassword, updateGuestPermissions } from '@/api/auth.js'
import { isPushSupported, subscribeToPush, unsubscribeFromPush, isCurrentlySubscribed, getPushPermissionState } from '@/utils/pushNotification.js'

export default {
  name: 'SettingsPage',
  data() {
    return {
      isGuest: false,
      hasGuestPassword: false,
      showPasswordModal: false,
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
      showOldPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
      isSubmitting: false,
      toastVisible: false,
      toastMessage: '',
      toastType: 'success',
      // 访客密码设置
      showGuestPasswordModal: false,
      mainPasswordForGuest: '',
      guestPassword: '',
      confirmGuestPassword: '',
      showMainPasswordForGuest: false,
      showGuestPassword: false,
      showConfirmGuestPassword: false,
      isSubmittingGuest: false,
      // 推送通知
      pushSupported: false,
      pushEnabled: false,
      pushDenied: false,
      pushLoading: false,
      // 访客权限
      guestCanSpend: true,
      guestCanRecharge: true,
      guestSpendLoading: false,
      guestRechargeLoading: false
    }
  },
  mounted() {
    this.loadUserInfo()
    this.checkPushStatus()
  },
  methods: {
    loadUserInfo() {
      try {
        const stored = localStorage.getItem('user')
        if (stored) {
          const data = JSON.parse(stored)
          this.isGuest = data.isGuest || false
          this.hasGuestPassword = data.hasGuestPassword || false
          this.guestCanSpend = data.guestCanSpend !== false
          this.guestCanRecharge = data.guestCanRecharge !== false
        }
      } catch (e) {
        console.error('获取用户信息失败', e)
      }
    },
    goBack() {
      this.$emit('close')
    },
    openPasswordModal() {
      if (this.isGuest) {
        this.showToast('访客无法修改密码', 'error')
        return
      }
      this.showPasswordModal = true
    },
    closePasswordModal() {
      this.showPasswordModal = false
      this.oldPassword = ''
      this.newPassword = ''
      this.confirmPassword = ''
      this.showOldPassword = false
      this.showNewPassword = false
      this.showConfirmPassword = false
    },
    showToast(message, type = 'success') {
      this.toastMessage = message
      this.toastType = type
      this.toastVisible = true
      setTimeout(() => {
        this.toastVisible = false
      }, 2500)
    },
    async submitChangePassword() {
      if (!this.newPassword) {
        this.showToast('请输入新密码', 'error')
        return
      }

      // 确认提示
      if (!confirm('修改密码后，所有已登录的设备将被强制退出，需要重新登录。确认继续吗？')) {
        return
      }

      this.isSubmitting = true

      try {
        const res = await changePassword(this.newPassword)

        if (res.code === 0) {
          this.showToast('密码修改成功！所有设备已退出登录，请使用新密码重新登录', 'success')
          this.closePasswordModal()
          // 延迟后退出登录
          setTimeout(() => {
            localStorage.removeItem('user')
            localStorage.removeItem('token')
            localStorage.removeItem('username')
            window.location.href = '/#/login'
            window.location.reload()
          }, 2000)
        } else {
          this.showToast(res.msg || '密码修改失败', 'error')
        }
      } catch (e) {
        this.showToast('网络错误，请稍后重试', 'error')
      } finally {
        this.isSubmitting = false
      }
    },
    closeGuestPasswordModal() {
      this.showGuestPasswordModal = false
      this.mainPasswordForGuest = ''
      this.guestPassword = ''
      this.showMainPasswordForGuest = false
      this.showGuestPassword = false
    },
    async submitGuestPassword() {
      this.isSubmittingGuest = true

      try {
        const res = await setGuestPassword(this.guestPassword)

        if (res.code === 0) {
          this.hasGuestPassword = this.guestPassword && this.guestPassword.length > 0
          // 更新localStorage
          try {
            const stored = localStorage.getItem('user')
            if (stored) {
              const userData = JSON.parse(stored)
              userData.hasGuestPassword = this.hasGuestPassword
              localStorage.setItem('user', JSON.stringify(userData))
            }
          } catch (e) {
            // ignore
          }
          this.showToast(this.guestPassword ? '访客密码设置成功' : '访客密码已清除', 'success')
          this.closeGuestPasswordModal()
        } else {
          this.showToast(res.msg || '设置失败', 'error')
        }
      } catch (e) {
        this.showToast('网络错误，请稍后重试', 'error')
      } finally {
        this.isSubmittingGuest = false
      }
    },
    async toggleGuestCanSpend() {
      if (this.guestSpendLoading) return
      this.guestSpendLoading = true
      const newVal = !this.guestCanSpend
      try {
        const res = await updateGuestPermissions({ guestCanSpend: newVal })
        if (res.code === 0) {
          this.guestCanSpend = newVal
          this.updateLocalStorage('guestCanSpend', newVal)
          this.showToast(newVal ? '已允许访客花费余额' : '已禁止访客花费余额', 'success')
        } else {
          this.showToast(res.msg || '设置失败', 'error')
        }
      } catch (e) {
        this.showToast('网络错误，请稍后重试', 'error')
      } finally {
        this.guestSpendLoading = false
      }
    },
    async toggleGuestCanRecharge() {
      if (this.guestRechargeLoading) return
      this.guestRechargeLoading = true
      const newVal = !this.guestCanRecharge
      try {
        const res = await updateGuestPermissions({ guestCanRecharge: newVal })
        if (res.code === 0) {
          this.guestCanRecharge = newVal
          this.updateLocalStorage('guestCanRecharge', newVal)
          this.showToast(newVal ? '已允许访客充值' : '已禁止访客充值', 'success')
        } else {
          this.showToast(res.msg || '设置失败', 'error')
        }
      } catch (e) {
        this.showToast('网络错误，请稍后重试', 'error')
      } finally {
        this.guestRechargeLoading = false
      }
    },
    updateLocalStorage(key, value) {
      try {
        const stored = localStorage.getItem('user')
        if (stored) {
          const userData = JSON.parse(stored)
          userData[key] = value
          localStorage.setItem('user', JSON.stringify(userData))
        }
      } catch (e) {
        // ignore
      }
    },
    async checkPushStatus() {
      this.pushSupported = isPushSupported()
      if (this.pushSupported) {
        this.pushEnabled = await isCurrentlySubscribed()
        this.pushDenied = getPushPermissionState() === 'denied'
      }
    },
    async togglePushNotification() {
      if (this.pushLoading) return
      if (!this.pushSupported) {
        this.showToast('当前环境不支持推送通知', 'error')
        return
      }

      this.pushLoading = true
      try {
        if (this.pushEnabled) {
          await unsubscribeFromPush()
          this.pushEnabled = false
          this.showToast('推送通知已关闭', 'success')
        } else {
          const result = await subscribeToPush()
          if (result) {
            this.pushEnabled = true
            this.pushDenied = false
            this.showToast('推送通知已开启', 'success')
          } else {
            this.pushDenied = getPushPermissionState() === 'denied'
            if (this.pushDenied) {
              this.showToast('通知权限被拒绝，请在浏览器设置中开启', 'error')
            }
          }
        }
      } catch (e) {
        console.error('推送通知操作失败:', e)
        this.showToast('操作失败，请稍后重试', 'error')
      } finally {
        this.pushLoading = false
      }
    }
  }
}
</script>

<style scoped>
.settings-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #F2F2F7 0%, #E5E5EA 100%);
  padding-bottom: 40px;
}

/* iOS 18 大标题导航栏 */
.nav-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  color: #007AFF;
  font-size: 17px;
  font-weight: 400;
  cursor: pointer;
  padding: 0;
}

.back-btn:active {
  opacity: 0.6;
}

.page-title {
  font-size: 34px;
  font-weight: 700;
  color: #000;
  padding: 8px 20px 20px;
  letter-spacing: -0.5px;
}

/* 设置区块 */
.settings-section {
  padding: 0 16px;
  margin-bottom: 32px;
}

.section-header {
  font-size: 13px;
  font-weight: 400;
  color: #6D6D72;
  text-transform: uppercase;
  letter-spacing: -0.08px;
  padding: 0 12px 8px;
}

.settings-card {
  background: #FFFFFF;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.settings-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.settings-item:active {
  background-color: #E5E5EA;
}

.item-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.item-icon {
  width: 30px;
  height: 30px;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.password-icon {
  background: linear-gradient(135deg, #34C759 0%, #30B350 100%);
}

.guest-icon {
  background: linear-gradient(135deg, #007AFF 0%, #0062CC 100%);
}

.notification-icon {
  background: linear-gradient(135deg, #FF9500 0%, #FF6B00 100%);
}

.spend-icon {
  background: linear-gradient(135deg, #FF9500 0%, #FF6B00 100%);
}

.recharge-icon {
  background: linear-gradient(135deg, #5856D6 0%, #4240B0 100%);
}

.notification-item:active .toggle-switch {
  background-color: inherit;
}

.toggle-switch {
  width: 51px;
  height: 31px;
  border-radius: 15.5px;
  background: #E5E5EA;
  position: relative;
  cursor: pointer;
  transition: background-color 0.3s ease;
  flex-shrink: 0;
}

.toggle-switch.active {
  background: #34C759;
}

.toggle-switch.loading {
  opacity: 0.6;
  pointer-events: none;
}

.toggle-knob {
  width: 27px;
  height: 27px;
  border-radius: 50%;
  background: #FFFFFF;
  position: absolute;
  top: 2px;
  left: 2px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease;
}

.toggle-switch.active .toggle-knob {
  transform: translateX(20px);
}

.settings-item-hint {
  padding: 4px 16px 12px 60px;
  font-size: 12px;
  color: #8E8E93;
  line-height: 1.4;
}

.settings-item-hint.warning {
  color: #FF3B30;
}

.item-disabled-hint {
  font-size: 12px;
  color: #8E8E93;
  margin-left: 4px;
}

.item-status {
  font-size: 12px;
  color: #8E8E93;
  margin-left: 8px;
}

.item-label {
  font-size: 17px;
  font-weight: 400;
  color: #000;
}

.item-right {
  display: flex;
  align-items: center;
}

/* 修改密码弹窗 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-container {
  background: #FFFFFF;
  border-radius: 16px;
  width: 100%;
  max-width: 340px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 0.5px solid #E5E5EA;
}

.modal-title {
  font-size: 17px;
  font-weight: 600;
  color: #000;
}

.modal-close {
  background: #F2F2F7;
  border: none;
  width: 28px;
  height: 28px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.modal-close:active {
  background: #E5E5EA;
}

.modal-body {
  padding: 20px;
}

.input-group {
  margin-bottom: 16px;
}

.input-group label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #6D6D72;
  margin-bottom: 8px;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-wrapper input {
  width: 100%;
  height: 44px;
  padding: 0 44px 0 14px;
  font-size: 17px;
  color: #000;
  background: #F2F2F7;
  border: none;
  border-radius: 10px;
  outline: none;
  transition: background-color 0.2s ease;
}

.input-wrapper input:focus {
  background: #E8E8ED;
}

.input-wrapper input::placeholder {
  color: #C7C7CC;
}

.toggle-password {
  position: absolute;
  right: 10px;
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.password-tips {
  background: #F2F2F7;
  border-radius: 10px;
  padding: 12px 14px;
  margin-top: 8px;
}

.password-tips p {
  font-size: 13px;
  color: #8E8E93;
  margin: 0;
  line-height: 1.6;
}

.modal-footer {
  display: flex;
  gap: 10px;
  padding: 16px 20px 20px;
}

.modal-footer button {
  flex: 1;
  height: 44px;
  border-radius: 10px;
  font-size: 17px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-cancel {
  background: #F2F2F7;
  color: #007AFF;
}

.btn-cancel:active {
  background: #E5E5EA;
}

.btn-confirm {
  background: #007AFF;
  color: #FFFFFF;
}

.btn-confirm:active {
  background: #0062CC;
}

.btn-confirm:disabled {
  background: #C7C7CC;
  cursor: not-allowed;
}

/* Toast 提示 */
.toast {
  position: fixed;
  top: 60px;
  left: 50%;
  transform: translateX(-50%);
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 500;
  z-index: 2000;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
}

.toast.success {
  background: #34C759;
  color: #FFFFFF;
}

.toast.error {
  background: #FF3B30;
  color: #FFFFFF;
}

/* 动画 */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.modal-fade-enter-active .modal-container,
.modal-fade-leave-active .modal-container {
  transition: transform 0.25s ease, opacity 0.25s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-from .modal-container,
.modal-fade-leave-to .modal-container {
  transform: scale(0.95);
  opacity: 0;
}

.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: all 0.3s ease;
}

.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-10px);
}
</style>



